import React, { useMemo, useState } from 'react';
import RouterLink from './RouterLink';
import {
  UserCheck, Hourglass, Users, UserPlus, Trophy, Target, Radio, Activity, X, CheckCircle2, AlertTriangle, ArrowRight, ArrowDown,
} from 'lucide-react';
import {
  fmtDay, fmtSpan, fmtInt, fmtSigned, niceScale, toggleHidden,
} from '../../lib/slotStatsView';
import {
  fmtRatePct, fmtPp, sourceColor, cohortWindow, localDay, dayOf, signalText, notReporting, trendRows, pendingShort, batchName, rateN, ofText,
} from '../../lib/followBackView';
import {
  ChartCard, DataTableView, Delta, EmptyPanel, Segmented,
} from './ChartKit';
import { Sparkline } from './StatTiles';
import {
  RateTrendChart, EngagedStackChart, SourceLegend, TargetRankingChart, rateScale, sharedAxisWidth, useHighlight,
} from './FollowBackCharts';
import useIsPhone from '../../lib/useIsPhone';
import { cn } from '@/lib/utils';

/*
 * r16m (2026-10-20): the pieces the Follow-back card (Slot Stats) and the Follow-back page share - the range control labelled with
 * the cohort dates, the signal strip, the KPI tiles, the trend card (rate by source type + people engaged, one legend, a table twin),
 * the target card, and the honest empty states (design 3.7). Every number comes from lib/followBackView.js.
 */

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

const logged = new Set();
/** One console line per distinct message and page load (deep logging without a line per re-render). */
export function fbLog(level, line) {
  if (logged.has(line)) return;
  logged.add(line);
  // eslint-disable-next-line no-console
  (level === 'warn' ? console.warn : console.info)(line);
}
const spanOf = (d) => (d && d.from && d.to ? fmtSpan(d.from, d.to) : null);
const plural = (n, word) => `${fmtInt(n)} ${word}${n === 1 ? '' : 's'}`;

/** The range options with their cohort dates ("7 days · 6–13 Oct"); `ranges`: [7, 30, 'all'] or with 90. */
export function rangeOptions(m, ranges, { short = false } = {}) {
  return ranges.map((r) => {
    if (!m) return { value: r, label: r === 'all' ? 'All time' : `${r} days` };
    const w = cohortWindow(m.asOf, m.windowDays, r);
    if (r === 'all') {
      const since = isNum(m.trackingSince) ? localDay(m, m.trackingSince) : null;
      return { value: r, label: since && !short ? `All time · since ${fmtDay(since)}` : 'All time' };
    }
    const from = dayOf(w.from, m.tz);
    const to = dayOf(w.to - 1, m.tz);
    return { value: r, label: short ? `${r} days` : `${r} days · ${fmtSpan(from, to, false)}` };
  });
}

/* ------------------------------------------------------------------ the signal strip */

export function SignalStrip({ m, group, testid = 'fb-signal' }) {
  const s = signalText(m);
  const missing = group ? notReporting(m) : [];
  const Icon = s.ok ? CheckCircle2 : AlertTriangle;
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-3.5 py-2 text-xs',
      s.ok ? 'border-white/5 bg-white/[.02] text-muted-foreground' : 'border-[hsl(var(--warning)/.35)] bg-[hsl(var(--warning)/.08)] text-foreground')}
      data-testid={testid} data-ok={s.ok ? 'yes' : 'no'}>
      <span className="inline-flex items-center gap-1.5">
        <Icon className={cn('h-3.5 w-3.5 shrink-0', s.ok ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--warning))]')} aria-hidden />
        <span>{s.text}</span>
      </span>
      {isNum(m.signal.lastReadAt) && (
        <span className="text-muted-foreground" data-testid={`${testid}-last`}>
          last read {fmtDay(localDay(m, m.signal.lastReadAt), 'medium')}, {new Date(m.signal.lastReadAt + m.tz * 60000).toISOString().slice(11, 16)} ({m.tzLabel})
        </span>
      )}
      {missing.length > 0 && (
        <span className="text-muted-foreground" data-testid={`${testid}-missing`}>
          Not read in the last 24 h: <span className="text-foreground">{missing.join(', ')}</span>
        </span>
      )}
      {m.signal.note && !missing.length && isNum(m.signal.lastReadAt) && <span className="text-muted-foreground">{m.signal.note}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ KPI tiles */

function Tile({ icon: Icon, label, children, className, testid }) {
  return (
    <div className={cn('aurora-card flex min-w-0 flex-col gap-1.5 p-4', className)} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        <span>{label}</span>
      </div>
      {children}
    </div>
  );
}

const Value = ({ children, muted, testid, big }) => (
  <p className={cn('font-sans font-semibold leading-none tracking-tight', big ? 'text-[2.1rem]' : 'text-[1.6rem]',
    muted ? 'text-muted-foreground' : 'text-foreground')} data-testid={testid}>
    {children}
  </p>
);

/** The hero: the matured cohort's rate with its dates and the like-for-like change (never the maturing figure). */
export function RateHeroTile({ m, h, spark, testid = 'fb-tile-rate' }) {
  const days = spanOf(h.days);
  const prevDays = h.previous ? spanOf(h.previous.days) : null;
  let body;
  if (h.state === 'noTouches' || h.state === 'idle') {
    body = <p className="text-xs text-muted-foreground" data-testid={`${testid}-why`}>{h.state === 'idle' ? `Nobody was engaged ${days || 'in this period'}.` : 'Nothing attributed yet.'}</p>;
  } else if (h.state === 'emptyCohort') {
    body = (
      <p className="text-xs text-muted-foreground text-pretty" data-testid={`${testid}-why`}>
        Nobody was engaged {days || 'in this period'}{h.target ? ` from ${h.target}` : ''}, so there is no matured rate for it.
        {h.maturing.engaged > 0 ? ' People engaged since are still maturing (next tile).' : ''}
      </p>
    );
  } else if (h.state === 'notMatured') {
    body = (
      <p className="text-xs text-muted-foreground text-pretty" data-testid={`${testid}-why`}>
        Nothing has matured yet{h.firstMatures ? `: the first engagements mature on ${fmtDay(h.firstMatures, 'medium')}` : ''}.
        A rate counts people only once they have had {m.windowDays} days to follow back.
      </p>
    );
  } else {
    body = (
      <>
        <p className="text-xs text-muted-foreground tnum" data-testid={`${testid}-detail`}>
          {h.rate === null
            ? `${fmtInt(h.engaged)} people engaged ${days || ''}: follow-backs not checked`
            : `${fmtInt(h.followedBack)} of ${fmtInt(h.checked)} people engaged ${days || ''} followed back within ${m.windowDays} days`
              + `${h.checked < h.engaged ? ` (${fmtInt(h.engaged - h.checked)} more were engaged on slots whose list was not read after them: left out of the rate)` : ''}`}
        </p>
        {h.previous && h.rate !== null && (
          h.deltaPp !== null ? (
            <p className="flex flex-wrap items-center gap-x-1.5 text-sm font-semibold text-foreground tnum" data-testid={`${testid}-delta`}>
              <Delta value={h.deltaPp} text={fmtPp(h.deltaPp)} />
              <span className="font-normal text-muted-foreground">vs {prevDays} ({fmtRatePct(h.previous.rate)}, n {fmtInt(rateN(h.previous))})</span>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid={`${testid}-delta`}>
              {h.previous.why === 'notCovered'
                ? `No change shown: ${prevDays} is not covered by a follow-back read.`
                : h.previous.why === 'beforeTracking'
                  ? `No change shown: follow-back tracking started ${fmtDay(h.trackingSinceDay, 'medium')}, so ${prevDays} has no matured figure to compare with.`
                  : `No change shown: nobody was engaged ${prevDays || 'in the period before'}.`}
            </p>
          )
        )}
      </>
    );
  }
  return (
    <Tile icon={UserCheck} label="Follow-back rate" className="col-span-2" testid={testid}>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <Value big muted={h.rate === null} testid={`${testid}-value`}>{fmtRatePct(h.rate)}</Value>
          {days && (h.state === 'ok' || h.state === 'noSignal') && (
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`${testid}-cohort`}>engaged {days} · matured</p>
          )}
        </div>
        {spark && spark.length >= 2 && <Sparkline values={spark} testid={`${testid}-spark`} />}
      </div>
      {body}
    </Tile>
  );
}

/** Still maturing: the last W days' rate so far, with its n - its own tile, never the headline. */
export function MaturingTile({ m, h, testid = 'fb-tile-maturing' }) {
  const mt = h.maturing;
  const days = spanOf(mt.days);
  return (
    <Tile icon={Hourglass} label="Still maturing" className="col-span-2 sm:col-span-1" testid={testid}>
      <Value muted={mt.rate === null} testid={`${testid}-value`}>
        {mt.engaged > 0 ? fmtRatePct(mt.rate) : '-'}
        {mt.engaged > 0 && mt.rate !== null && <span className="ml-1.5 text-sm font-normal text-muted-foreground">so far</span>}
      </Value>
      <p className="text-xs text-muted-foreground tnum" data-testid={`${testid}-detail`}>
        {mt.engaged > 0
          ? `n ${fmtInt(mt.engaged)} engaged ${days || ''}${mt.rate !== null ? ` · ${fmtInt(mt.followedBack)}${mt.checked < mt.engaged ? ` of ${fmtInt(mt.checked)} checked` : ''} so far` : ''}`
          : `Nobody engaged ${days || 'in the last days'}.`}
      </p>
      <p className="text-[11px] text-muted-foreground">Less than {m.windowDays} days to follow back yet: it can still rise.</p>
    </Tile>
  );
}

function CountTile({ icon, label, value, prev, prevDays, testid }) {
  const d = isNum(value) && isNum(prev) ? value - prev : null;
  return (
    <Tile icon={icon} label={label} testid={testid}>
      <Value muted={!isNum(value)} testid={`${testid}-value`}>{isNum(value) ? fmtInt(value) : '-'}</Value>
      {d !== null && prevDays ? (
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground tnum" data-testid={`${testid}-delta`}>
          <Delta value={d} text={fmtSigned(d)} className="font-semibold text-foreground" />
          <span>vs {prevDays}</span>
        </p>
      ) : <p className="text-xs text-muted-foreground">in the matured period</p>}
    </Tile>
  );
}

/** The best source type (n >= minRankN), with the others' rate and n. */
function BestSourceTile({ m, h, sources, best, testid = 'fb-tile-best-source' }) {
  const others = sources.filter((s) => s !== best && s.engaged > 0);
  return (
    <Tile icon={Trophy} label="Best source type" className="col-span-2 sm:col-span-1" testid={testid}>
      {!h.covered && h.engaged > 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`${testid}-value`}>- (follow-backs not checked)</p>
      ) : best ? (
        <>
          <p className="flex min-w-0 items-center gap-2 text-sm font-semibold text-foreground" data-testid={`${testid}-value`}>
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: best.color }} aria-hidden />
            <span className="truncate">{best.label}</span>
            <span className="ml-auto text-lg tnum">{fmtRatePct(best.rate)}</span>
          </p>
          <p className="text-xs text-muted-foreground tnum">n {fmtInt(rateN(best))}</p>
          <ul className="space-y-0.5 text-xs text-muted-foreground tnum" data-testid={`${testid}-others`}>
            {others.slice(0, 3).map((s) => (
              <li key={s.key} className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-1.5"
                title={s.rankable ? undefined : `n ${fmtInt(rateN(s))}: fewer than ${m.minRankN}, too few to rank`}>
                <span className="inline-block h-2 w-2 shrink-0 rounded-[2px]" style={{ background: s.color }} aria-hidden />
                <span className="truncate">{s.label}</span>
                <span className={s.rankable ? 'text-foreground/90' : ''}>{fmtRatePct(s.rate)}</span>
                <span className="whitespace-nowrap">n {fmtInt(rateN(s))}{!s.rankable ? '*' : ''}</span>
              </li>
            ))}
          </ul>
          {others.slice(0, 3).some((s) => !s.rankable) && <p className="text-[11px] text-muted-foreground">* fewer than {m.minRankN}: too few to rank</p>}
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid={`${testid}-value`}>
          {sources.some((s) => s.engaged > 0) ? `Too few to rank: no source type has n ${m.minRankN} or more with a known rate.` : '-'}
        </p>
      )}
    </Tile>
  );
}

function BestTargetTile({ m, h, ranking, testid = 'fb-tile-best-target' }) {
  const b = ranking.best;
  return (
    <Tile icon={Target} label="Best target" className="col-span-2 sm:col-span-1" testid={testid}>
      {!h.covered && h.engaged > 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`${testid}-value`}>- (follow-backs not checked)</p>
      ) : b ? (
        <>
          <p className="flex min-w-0 items-baseline gap-2 text-sm font-semibold text-foreground" data-testid={`${testid}-value`}>
            <span className="truncate" title={b.target}>{b.target}</span>
            <span className="ml-auto text-lg tnum">{fmtRatePct(b.rate)}</span>
          </p>
          <p className="text-xs text-muted-foreground tnum">n {fmtInt(rateN(b))} · {fmtInt(b.followedBack)} followed back</p>
          <p className="text-[11px] text-muted-foreground">of {plural(ranking.rows.filter((r) => r.rankable).length, 'target')} with n {m.minRankN}+</p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid={`${testid}-value`}>
          {ranking.rows.length ? `Too few to rank: no target has n ${m.minRankN} or more.` : '-'}
        </p>
      )}
    </Tile>
  );
}

function SlotsReportingTile({ m, testid = 'fb-tile-slots' }) {
  const s = m.signal;
  const missing = notReporting(m);
  return (
    <Tile icon={Radio} label="Slots reporting" className="col-span-2 sm:col-span-1" testid={testid}>
      <Value muted={!isNum(s.slotsReadOk24h)} testid={`${testid}-value`}>
        {isNum(s.slotsReadOk24h) ? `${fmtInt(s.slotsReadOk24h)}/${fmtInt(s.slotsTotal)}` : '-'}
      </Value>
      <p className="text-xs text-muted-foreground">follow-back list read in the last 24 h</p>
      {missing.length > 0 && <p className="text-xs text-muted-foreground">Not: <span className="text-foreground">{missing.join(', ')}</span></p>}
    </Tile>
  );
}

/**
 * The KPI row (design 3.7 item 3): rate (hero), still maturing, people engaged, followed back, best source type, best target, and
 * slots reporting on a group. `h` = headline(m), `sources` = sourceRows(m), `best` = bestOf(sources), `ranking` = targetRanking(m).
 */
export function KpiRow({ m, h, sources, best, ranking, spark, group, compact }) {
  const prevDays = h.previous ? spanOf(h.previous.days) : null;
  const prevOk = h.previous && h.previous.engaged > 0 ? h.previous : null;
  return (
    <section aria-label="Follow-back figures" data-testid="fb-kpis"
      className={cn('grid grid-cols-2 gap-3', compact ? 'lg:grid-cols-3' : 'lg:grid-cols-4 2xl:grid-cols-6')}>
      <RateHeroTile m={m} h={h} spark={spark} />
      <MaturingTile m={m} h={h} />
      <CountTile icon={Users} label="People engaged" value={h.engaged} prev={prevOk ? prevOk.engaged : null} prevDays={prevOk ? prevDays : null}
        testid="fb-tile-engaged" />
      <CountTile icon={UserPlus} label="Followed back" value={h.rate === null ? null : h.followedBack}
        prev={prevOk && h.previous.rate !== null ? prevOk.followedBack : null} prevDays={prevOk ? prevDays : null} testid="fb-tile-followed" />
      <BestSourceTile m={m} h={h} sources={sources} best={best} />
      {!compact && <BestTargetTile m={m} h={h} ranking={ranking} />}
      {group && <SlotsReportingTile m={m} />}
    </section>
  );
}

/* ------------------------------------------------------------------ counts behind the rate (the table twins carry them) */

export function CountsNote({ m, testid = 'fb-counts' }) {
  const t = m.totals;
  const items = [
    ['Ambiguous credits (counted)', t.ambiguous, 'the read before the follow-back was before the engagement'],
    [`Late follow-backs (after ${m.windowDays} days, not credited)`, t.late, null],
    ['Before tracking started (left out)', t.beforeTracking, 'engaged before the slot\'s first follow-back read'],
    ['Already following us (left out)', t.preFollowersExcluded, 'their button read "Follow back" when we engaged'],
    ['Outcome unknown (left out)', t.unknownOutcome, 'a tap was sent but its result was not confirmed'],
    ...(isNum(t.checked) && t.checked < t.engaged
      ? [['On slots not read after (left out of the rate)', t.engaged - t.checked, 'engaged on slots whose follow-back list was not read after them']] : []),
  ];
  return (
    <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2" data-testid={testid}>
      {items.map(([label, v, why]) => (
        <div key={label} className="flex items-baseline justify-between gap-3 border-t border-white/5 py-1">
          <dt className="text-muted-foreground" title={why || undefined}>{label}</dt>
          <dd className="tnum text-foreground">{fmtInt(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------------------ the trend card */

/**
 * Rate by source type over time (lines) and people engaged by source type (stacked bars) on one x axis, one legend for both, and
 * the table twin. `trend` = trendRows(m). A phone starts By week: 38 days x 5 lines in about 165 px of plot is unreadable.
 */
export function TrendCard({ m, compact = false, withStack = true, testid = 'fb-trend' }) {
  const isPhone = useIsPhone();
  const [hidden, setHidden] = useState([]);
  const [hi, onHi] = useHighlight();
  const [picked, setGroup] = useState(null);
  const group = picked || (isPhone ? 'week' : 'auto');
  // which chart the pointer or the keyboard is on: only ITS tooltip shows (the synced twin's does not), and none once the pointer
  // and the focus have left - Recharts keeps a keyboard tooltip active after the focus leaves, which then showed in BOTH charts
  const [over, setOver] = useState(null);
  const trend = useMemo(() => trendRows(m, group), [m, group]);
  const { rows, keys, weekly } = trend;
  const serverWeekly = m.series.some((b) => b.granularity === 'week');
  const shown = keys.filter((k) => !hidden.includes(k));
  const view = useMemo(() => {
    const lined = shown.filter((k) => trend.lineKeys.includes(k));
    const vals = rows.flatMap((r) => [...lined.map((k) => (r.cells[k].plotted ? r.cells[k].rate : null)), r.all.plotted ? r.all.rate : null])
      .filter(isNum).map((v) => v * 100);
    const rSc = rateScale(Math.max(1, ...vals), compact ? 4 : 5);
    const stackMax = Math.max(1, ...rows.map((r) => shown.reduce((s, k) => s + (r.parts[k] || 0), 0)));
    const eSc = niceScale(0, stackMax, { count: 4, zero: true });
    return { rSc, eSc, yW: sharedAxisWidth(rSc, eSc) };
  }, [rows, shown, compact, trend.lineKeys]);
  const first = rows[0];
  const last = rows[rows.length - 1];
  const lit = hi && shown.includes(hi) ? hi : null;
  const labelOf = (k) => (k === 'OTHER' ? 'Other sources' : m.labelOf(k));
  if (!rows.length) {
    return (
      <ChartCard icon={Activity} title="Follow-back rate by source type" testid={testid}>
        <EmptyPanel testid={`${testid}-empty`}>No engagement in this range.</EmptyPanel>
      </ChartCard>
    );
  }
  const unit = weekly ? 'week' : 'day';
  const bucketText = (r) => (weekly ? fmtSpan(r.from, r.to) : fmtDay(r.date, 'long'));
  const columns = [
    {
      key: 'date', label: weekly ? 'Week engaged' : 'Day engaged',
      render: (r) => `${bucketText(r)}${r.note ? ` (${r.note}${r.drawn ? '' : ', not drawn'})` : ''}${r.maturing ? ' (maturing)' : ''}`,
    },
    ...keys.map((k) => ({
      key: k, label: labelOf(k), align: 'right',
      render: (r) => { const c = r.cells[k]; return c.engaged > 0 ? `${fmtRatePct(c.rate)} · ${ofText(c)}` : '-'; },
    })),
    {
      key: 'all', label: 'All sources', align: 'right',
      render: (r) => (r.all.engaged > 0 ? `${fmtRatePct(r.all.rate)} · ${ofText(r.all)}` : '-'),
    },
  ];
  const table = (
    <>
      <DataTableView caption="Follow-back rate and people engaged by source type" columns={columns} rows={[...trend.tableRows].reverse()} testid={`${testid}-datatable`} />
      <CountsNote m={m} testid={`${testid}-counts`} />
    </>
  );
  // what the chart leaves out, said once: part periods at its ends, and a source with too few people for a line
  const partsText = trend.parts.length
    ? ` Part ${unit}s are in the table only: ${trend.parts.map((r) => `${bucketText(r)} (${r.note})`).join(', ')}.`
    : '';
  const sparseText = trend.sparse.length
    ? ` ${trend.sparse.map(labelOf).join(' and ')} ${trend.sparse.length === 1 ? 'has' : 'have'} too few people per ${unit} for a line: in the bars, the tooltip and the table.`
    : '';
  return (
    <ChartCard icon={Activity} title="Follow-back rate by source type" testid={testid}
      subtitle={`${weekly ? 'By the week' : 'By the day'} people were engaged, ${fmtSpan(first.from, last.to)} (${m.tzLabel}). `
        + `A person counts when they follow back within ${m.windowDays} days; the last ${m.windowDays} days are still maturing (dashed). `
        + `A point needs ${m.minRankN} or more people of its source${trend.grouped && !picked ? (isPhone ? '; grouped by 7 days on a phone' : '; days are grouped by 7 here, a day alone holds too few people') : ''}.`
        + `${partsText}${sparseText}`}
      controls={serverWeekly ? null : (
        <Segmented label="Group the days" size="sm" value={weekly ? 'week' : 'day'} onChange={(v) => setGroup(v)} testidPrefix={`${testid}-group`}
          options={[{ value: 'day', label: 'By day' }, { value: 'week', label: 'By week' }]} />
      )}
      table={table}
      legend={(
        <SourceLegend keys={keys} hidden={hidden} hi={lit} labelOf={labelOf} onHi={onHi}
          onToggle={(k) => setHidden(toggleHidden(hidden, k, keys))} onShowAll={() => setHidden([])} testid={`${testid}-legend`} />
      )}>
      {rows.some((r) => r.all.rate !== null) ? (
        <RateTrendChart trend={trend} hidden={hidden} hi={lit} onHi={onHi} labelOf={labelOf} yW={view.yW} scale={view.rSc}
          height={compact ? 210 : 250} windowDays={m.windowDays} minN={m.minRankN} showTip={over === 'rate'} live={over === 'rate' || over === 'stack'} onOver={setOver}
          testid={`${testid}-rate`} />
      ) : (
        <EmptyPanel testid={`${testid}-no-rate`}>
          No follow-back rate to draw: no read of the follow-back list covers these days yet. The people engaged are below.
        </EmptyPanel>
      )}
      {withStack && (
        <div className="mt-3" data-testid={`${testid}-stack-wrap`}>
          <h3 className="mb-1 pl-5 text-xs font-medium text-muted-foreground">People engaged by source type</h3>
          <EngagedStackChart trend={trend} hidden={hidden} hi={lit} onHi={onHi} labelOf={labelOf} yW={view.yW} scale={view.eSc}
            height={compact ? 140 : 170} windowDays={m.windowDays} minN={m.minRankN} showTip={over === 'stack'} live={over === 'rate' || over === 'stack'} onOver={setOver}
            testid={`${testid}-stack`} />
        </div>
      )}
    </ChartCard>
  );
}

/* ------------------------------------------------------------------ the target card */

export function TargetCard({ m, ranking, onPick, compact: compactProp = false, testid = 'fb-targets' }) {
  const isPhone = useIsPhone();
  const compact = compactProp || isPhone;
  const labelOf = (k) => (k === 'OTHER' ? 'Other sources' : m.labelOf(k));
  if (!ranking.rows.length) {
    return (
      <ChartCard icon={Target} title="By target account" testid={testid}>
        <EmptyPanel testid={`${testid}-empty`}>No target engaged in the matured period.</EmptyPanel>
      </ChartCard>
    );
  }
  const columns = [
    { key: 'target', label: 'Target', render: (r) => r.target },
    {
      key: 'types', label: 'Source type',
      render: (r) => r.types.map((t) => t.label).join(', '),
    },
    { key: 'engaged', label: 'Engaged', align: 'right', render: (r) => fmtInt(r.engaged) },
    { key: 'fb', label: 'Followed back', align: 'right', render: (r) => (r.rate === null ? '-' : fmtInt(r.followedBack)) },
    { key: 'rate', label: 'Rate', align: 'right', render: (r) => `${fmtRatePct(r.rate)}${r.rankable ? '' : ' (too few)'}` },
    ...ranking.keys.map((k) => ({
      key: `k_${k}`, label: labelOf(k), align: 'right',
      render: (r) => { const c = r.parts[k]; return c && c.engaged > 0 ? `${fmtRatePct(c.rate)} · n ${fmtInt(rateN(c))}` : '-'; },
    })),
    { key: 'last', label: 'Last engaged', align: 'right', render: (r) => (r.lastEngagedAt ? fmtDay(localDay(m, r.lastEngagedAt), 'medium') : '-') },
    ...(onPick ? [{
      key: 'pick', label: '', render: (r) => (
        <button type="button" className="tap rounded-full border border-white/10 px-2 py-0.5 text-xs text-foreground hover:bg-white/5"
          onClick={() => onPick(r.target)} data-testid={`${testid}-pick-${r.target}`}>Show only</button>
      ),
    }] : []),
  ];
  const tableRows = ranking.rows.map((r) => ({ ...r, key: r.target }));
  return (
    <ChartCard icon={Target} title="By target account" testid={testid}
      subtitle={`Matured period. Each target's own rate and n (all its sources) are by its name and set the order; one bar per source type, `
        + `its figures in the tooltip and the table. A bar or target with fewer than ${m.minRankN} people engaged is faded: too few to rank (such targets are last).`
        + `${onPick ? ' Click a target to show only its figures.' : ''}`}
      table={(
        <>
          <DataTableView caption="Follow-back rate by target account" columns={columns} rows={tableRows} testid={`${testid}-datatable`} />
          <CountsNote m={m} testid={`${testid}-counts`} />
        </>
      )}
      legend={(
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid={`${testid}-legend`}>
          {ranking.keys.map((k) => (
            <li key={k} className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: sourceColor(k) }} aria-hidden />
              {labelOf(k)}
            </li>
          ))}
          <li className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-[3px]" aria-hidden
              style={{ background: 'color-mix(in srgb, var(--viz-src-1) 32%, var(--viz-surface))' }} />
            Faded: too few to rank (n &lt; {m.minRankN})
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3.5 border-t border-dashed" style={{ borderColor: 'var(--viz-ink-2)' }} aria-hidden />
            Average of the {ranking.rows.length} targets
          </li>
        </ul>
      )}>
      <TargetRankingChart ranking={ranking} labelOf={labelOf} minRankN={m.minRankN} onPick={onPick} compact={compact} testid={`${testid}-plot`} />
    </ChartCard>
  );
}

/* ------------------------------------------------------------------ empty / honest states */

/**
 * The whole-answer empty states (design 3.7): nothing attributed yet (with the date attribution started), or engaged with no
 * follow-back signal (rates "-", #92). Returns null when the figures can be drawn.
 */
export function EmptyNotice({ m, h, slotLabel, testid = 'fb-empty' }) {
  const days = spanOf(h.days);
  if (h.state === 'idle') {
    // a quiet period (or a target with nobody in this range): earlier figures exist, so this is not "attribution started"
    return (
      <EmptyPanel testid={`${testid}-idle`}>
        {h.target
          ? `Nobody was engaged from ${h.target} ${days ? `in ${days}` : 'in this period'} or since. Clear the target, or pick another range.`
          : `Nobody was engaged ${days ? `in ${days}` : 'in this period'} or since. The earlier figures are under All time.`}
      </EmptyPanel>
    );
  }
  if (h.state === 'noTouches') {
    const since = m.attributionSince || m.trackingSince;
    return (
      <EmptyPanel testid={`${testid}-no-touches`}>
        {since
          ? `Attribution started on ${fmtDay(localDay(m, since), 'medium')}; engagements before that have no source type.`
          : 'No engagement has been attributed yet: attribution starts with the slot\'s first session on the new worker.'}
        {' '}Figures appear once {slotLabel || 'the slot'} engages people from its target lists.
      </EmptyPanel>
    );
  }
  return null;
}

/** The "no signal" line under the KPIs: engaged, but no read covers the period (rate "-", never 0%). */
export function NoSignalNote({ m, h, testid = 'fb-no-signal' }) {
  if (h.state !== 'noSignal') return null;
  const last = isNum(m.signal.lastReadAt) ? fmtDay(localDay(m, m.signal.lastReadAt), 'medium') : null;
  const one = m.kind === 'slot';
  const missing = one ? [] : notReporting(m);
  return (
    <p className="flex items-start gap-2 rounded-xl border border-[hsl(var(--warning)/.35)] bg-[hsl(var(--warning)/.08)] px-3.5 py-2 text-sm text-foreground"
      role="status" data-testid={testid}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--warning))]" aria-hidden />
      <span>
        {last ? `Follow-backs not checked since ${last}` : 'Follow-backs not checked yet'}: turn on #92 &quot;Enable Tracking of Follow Backs
        from each Source?&quot; for {one ? 'this slot' : (missing.length ? `the slots not read (${missing.join(', ')})` : 'these slots')}. The rate
        stays &quot;-&quot; until a read covers the period; it is never shown as 0%.
      </span>
    </p>
  );
}

/**
 * Slots that engage NOBODY in batch mode (review 2026-10-20): PENDING or DETACHED on a batch's phones (CONFLICT, fail closed for
 * the whole session). Said at the top, where it is seen, with the way out (Join). `stuck`: [{ slot, status, why }]; `onJoin` (the
 * page: scroll to the batch panel) or `joinHref` (the slot card: the page's batch panel).
 */
export function StuckAlert({ batch, stuck, onJoin, joinHref, slotView = false, testid = 'fb-stuck' }) {
  if (!batch || !stuck || !stuck.length) return null;
  const list = stuck.map((p) => `${p.slot} (${pendingShort(p)})`).join(', ');
  const action = slotView ? 'Join it on the Follow-back page' : `Join ${stuck.length === 1 ? 'it' : 'them'} in the batch panel`;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-[hsl(var(--warning)/.45)] bg-[hsl(var(--warning)/.1)] px-3.5 py-2 text-sm text-foreground"
      role="status" data-testid={testid}>
      <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--warning))]" aria-hidden />
      <span className="min-w-0 flex-1 basis-64 text-pretty">
        {slotView
          ? `${stuck[0].slot} engages nobody in batch mode: its phones are ${batchName(batch)}'s, but it is ${stuck[0].status === 'DETACHED' ? 'detached from it' : 'not a member'}.`
          : `${stuck.length === 1 ? '1 slot' : `${stuck.length} slots`} on ${batchName(batch)}'s phones ${stuck.length === 1 ? 'engages' : 'engage'} nobody in batch mode: ${list}.`}
      </span>
      {joinHref ? (
        <RouterLink to={joinHref} data-testid={`${testid}-join`}
          className="tap inline-flex h-8 items-center gap-1 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:opacity-90">
          {action}<ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </RouterLink>
      ) : onJoin ? (
        <button type="button" onClick={onJoin} data-testid={`${testid}-join`}
          className="tap inline-flex h-8 items-center gap-1 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:opacity-90">
          {action}<ArrowDown className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ the target chip */

export function TargetChip({ target, onClear, testid = 'fb-target-chip' }) {
  if (!target) return null;
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 py-0.5 pl-3 pr-1 text-xs text-foreground"
      data-testid={testid}>
      <Target className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
      <span className="truncate">Target: <span className="font-medium">{target}</span></span>
      <button type="button" onClick={onClear} aria-label={`Show every target again (clear ${target})`}
        className="tap inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/10" data-testid={`${testid}-clear`}>
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </span>
  );
}

/** A link to a group or slot page (the batch chip on the slot card). */
export function BatchLink({ batch, testid = 'fb-batch-link' }) {
  if (!batch) return null;
  return (
    <RouterLink to={`/follow-back?batch=${encodeURIComponent(batch.id)}`} data-testid={testid}
      className="tap inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[.03] px-2.5 py-0.5 text-[11px] font-medium text-foreground hover:bg-white/[.07]">
      Batch {batch.id}{isNum(batch.slotCount) ? ` · ${fmtInt(batch.slotCount)} slots` : ''}
    </RouterLink>
  );
}

